// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/AccreditationsSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiX, FiImage, FiUpload } from 'react-icons/fi';
import ImagePreview from '../common/ImagePreview';
import api from '../../../api';

// Toast Notification Component
const Toast = ({ message, type = 'success', onClose }) => {
  const bgColors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 max-w-sm w-full rounded-xl border p-4 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-200 ${bgColors[type]}`}>
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium">{message}</p>
        <button onClick={onClose} className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600">
          <FiX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const AccreditationsSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const accreditations = websiteData?.accreditations || { items: [] };

  // Local state parameters to fully insulate field inputs from parent state re-renders
  const [localTitle, setLocalTitle] = useState(accreditations.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(accreditations.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(accreditations.displayStyle || 'grid');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showGrades: accreditations.showGrades !== false,
    showDescriptions: accreditations.showDescriptions !== false
  });
  const [localItems, setLocalItems] = useState(accreditations.items || []);

  const [newItem, setNewItem] = useState({ name: '', fullName: '', grade: '', description: '', link: '' });
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadingField, setUploadingField] = useState(null); 
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Sync isolated state fields from parent only on clean initial mount or tenant context switch
  useEffect(() => {
    console.log('[AccreditationsSection] Syncing local inputs with baseline data models properties.');
    const sec = websiteData?.accreditations || {};
    setLocalTitle(sec.sectionTitle || '');
    setLocalSubtitle(sec.sectionSubtitle || '');
    setLocalDisplayStyle(sec.displayStyle || 'grid');
    setLocalCheckboxes({
      showGrades: sec.showGrades !== false,
      showDescriptions: sec.showDescriptions !== false
    });
    setLocalItems(sec.items || []);
  }, [websiteData?.accreditations, tenantId]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleToggle = () => {
    handleInputChange('accreditations', 'isActive', !accreditations.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[AccreditationsSection] Shifting parameter locally: Key [${field}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAddItem = () => {
    if (!newItem.name) return;
    
    const item = {
      _id: Date.now().toString(),
      name: newItem.name,
      fullName: newItem.fullName,
      grade: newItem.grade,
      description: newItem.description,
      link: newItem.link,
      logo: '',
      certificate: ''
    };
    
    const updatedItems = [...localItems, item];
    setLocalItems(updatedItems);
    handleInputChange('accreditations', 'items', updatedItems);
    setNewItem({ name: '', fullName: '', grade: '', description: '', link: '' });
    showToast('Accreditation item added', 'success');
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    setLocalItems(updatedItems);
    handleInputChange('accreditations', 'items', updatedItems);
    showToast('Accreditation item removed', 'success');
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setLocalItems(updatedItems);
  };

  const handleFileUpload = async (event, index, field) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showToast('File size should be less than 2MB', 'error');
      event.target.value = '';
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Please upload an image or PDF.', 'error');
      event.target.value = '';
      return;
    }

    // Set temporary local blob preview string
    const localBlobPreview = file.type === 'application/pdf' ? 'pdf-fallback.pdf' : URL.createObjectURL(file);
    const previewItems = [...localItems];
    previewItems[index] = { ...previewItems[index], [field]: localBlobPreview };
    setLocalItems(previewItems);

    setUploadingIndex(index);
    setUploadingField(field);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'accreditations');
      formData.append('itemIndex', index);
      formData.append('subField', field);

      const response = await api.post(`/api/website/${tenantId}/upload/accreditations`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const updatedItems = [...localItems];
        const fileUrl = response.data.data?.url || response.data.data;
        updatedItems[index] = { ...updatedItems[index], [field]: fileUrl };
        
        setLocalItems(updatedItems);
        handleInputChange('accreditations', 'items', updatedItems);
        showToast(`${field.charAt(0).toUpperCase() + field.slice(1)} uploaded successfully`, 'success');
      } else {
        showToast(response.data.message || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast(error.response?.data?.message || 'Failed to upload file', 'error');
    } finally {
      setUploadingIndex(null);
      setUploadingField(null);
      event.target.value = '';
    }
  };

  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (typeof urlPath === 'object') {
      if (urlPath.url) return urlPath.url.startsWith('/') ? (base ? `${base}${urlPath.url}` : urlPath.url) : urlPath.url;
      if (urlPath.fileId) return base ? `${base}/api/files/tenant-website/${urlPath.fileId}` : `/api/files/tenant-website/${urlPath.fileId}`;
      return '';
    }
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const removeFile = (index, field) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: '' };
    setLocalItems(updatedItems);
    handleInputChange('accreditations', 'items', updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[AccreditationsSection] Synchronizing variables to parent maps.');
    
    try {
      await handleInputChange('accreditations', 'sectionTitle', localTitle);
      await handleInputChange('accreditations', 'sectionSubtitle', localSubtitle);
      await handleInputChange('accreditations', 'displayStyle', localDisplayStyle);
      await handleInputChange('accreditations', 'showGrades', localCheckboxes.showGrades);
      await handleInputChange('accreditations', 'showDescriptions', localCheckboxes.showDescriptions);
      await handleInputChange('accreditations', 'items', localItems);

      const assembledPayload = {
        ...accreditations,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showGrades: localCheckboxes.showGrades,
        showDescriptions: localCheckboxes.showDescriptions,
        items: localItems
      };

      await handleSave('accreditations', assembledPayload);
      showToast('Accreditations saved successfully', 'success');
    } catch (error) {
      showToast('Failed to save accreditations', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Header action bar segment */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Accreditations & Recognitions</h2>
            <p className="text-sm text-slate-500 mt-0.5">Display institution quality badges, parameters ratings and certificates</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              onClick={handleToggle}
              type="button"
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                accreditations.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {accreditations.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>
            <button
              onClick={handleSaveClick}
              disabled={saving}
              type="button"
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Configurations Fields alignment wrapper card */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Accreditations & Recognitions"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Validated by prestigious bodies"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="grid">Grid Matrix</option>
                <option value="carousel">Carousel View Slider</option>
                <option value="list">Vertical Row List</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Grades Score', key: 'showGrades', checked: localCheckboxes.showGrades },
                { label: 'Show Descriptions Metadata', key: 'showDescriptions', checked: localCheckboxes.showDescriptions },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Accreditation Cards Listing Segment */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Accreditations</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">{localItems.length} elements</span>
          </div>

          <div className="space-y-4">
            {localItems.map((item, index) => {
              const resolvedLogo = resolveAssetUrl(item.logo);
              const resolvedCertificate = resolveAssetUrl(item.certificate);
              
              return (
                <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="p-4 sm:p-5 space-y-4">
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Badge Acronym Name</span>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="Name (e.g., NAAC)"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Full Title Name Description</span>
                        <input
                          type="text"
                          value={item.fullName || ''}
                          onChange={(e) => handleItemChange(index, 'fullName', e.target.value)}
                          placeholder="Full Title Name"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Grade Tier Ranking</span>
                        <input
                          type="text"
                          value={item.grade || ''}
                          onChange={(e) => handleItemChange(index, 'grade', e.target.value)}
                          placeholder="Grade (e.g., A++)"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                      <div className="col-span-1 sm:col-span-2 md:col-span-3 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Verification Link URL</span>
                        <input
                          type="text"
                          value={item.link || ''}
                          onChange={(e) => handleItemChange(index, 'link', e.target.value)}
                          placeholder="Verification lookup address anchor"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                      <div className="col-span-1 sm:col-span-2 md:col-span-3 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Description Abstract Scope</span>
                        <input
                          type="text"
                          value={item.description || ''}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Brief description summary text"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Dual upload section logograms vs certificate */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      
                      {/* Logo Picker wrapper */}
                      <div className="bg-slate-50 p-3 rounded-xl border border-slate-150 space-y-2">
                        <span className="block text-xs font-bold text-slate-500">Accreditation Badge Logo</span>
                        <div className="flex items-center space-x-3">
                          <div className="w-14 h-14 bg-white rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative group shadow-sm">
                            {resolvedLogo ? (
                              <>
                                <ImagePreview src={resolvedLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                                <button type="button" onClick={() => removeFile(index, 'logo')} className="absolute inset-0 bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                                  <FiX className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <FiImage className="w-5 h-5 text-slate-300" />
                            )}
                          </div>
                          <div>
                            <input
                              type="file"
                              id={`upload-logo-input-${index}`}
                              accept="image/jpeg,image/png,image/webp,image/svg+xml"
                              onChange={(e) => handleFileUpload(e, index, 'logo')}
                              className="hidden"
                            />
                            <label
                              htmlFor={`upload-logo-input-${index}`}
                              className={`inline-flex items-center px-2.5 py-1.5 border border-slate-200 rounded-lg text-[11px] font-bold text-slate-700 bg-white hover:bg-slate-50 cursor-pointer shadow-xs active:scale-95 transition-all ${
                                uploadingIndex === index && uploadingField === 'logo' ? 'opacity-60 cursor-not-allowed' : ''
                              }`}
                            >
                              <FiUpload className="mr-1.5 h-3.5 w-3.5 text-slate-500" />
                              {uploadingIndex === index && uploadingField === 'logo' ? 'Uploading...' : (resolvedLogo ? 'Change Logo' : 'Upload Logo')}
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Official Verification Certificate Document File */}
                      <div className="bg-slate-50 p-3 rounded-xl border border-slate-150 space-y-2">
                        <span className="block text-xs font-bold text-slate-600">Verification Certificate</span>
                        <div className="flex items-center space-x-3">
                          <div className="w-14 h-14 bg-white rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative group shadow-sm">
                            {resolvedCertificate ? (
                              <>
                                {resolvedCertificate.toLowerCase().endsWith('.pdf') || resolvedCertificate.includes('pdf-fallback') ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-[10px] text-red-700 font-bold uppercase rounded">
                                    PDF
                                  </div>
                                ) : (
                                  <img src={resolvedCertificate} alt="Certificate preview" className="w-full h-full object-contain p-1" />
                                )}
                                <button type="button" onClick={() => removeFile(index, 'certificate')} className="absolute inset-0 bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                                  <FiX className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <FiImage className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <input
                              type="file"
                              id={`upload-cert-input-${index}`}
                              accept=".pdf,image/jpeg,image/png,image/jpg,image/webp"
                              onChange={(e) => handleFileUpload(e, index, 'certificate')}
                              className="hidden"
                            />
                            <label
                              htmlFor={`upload-cert-input-${index}`}
                              className={`inline-flex items-center px-2.5 py-1.5 border border-slate-200 rounded-lg text-[11px] font-bold text-slate-700 bg-white hover:bg-slate-50 cursor-pointer shadow-xs active:scale-95 transition-all ${
                                uploadingIndex === index && uploadingField === 'certificate' ? 'opacity-60 cursor-not-allowed' : ''
                              }`}
                            >
                              <FiUpload className="mr-1.5 h-3.5 w-3.5 text-slate-500" />
                              {uploadingIndex === index && uploadingField === 'certificate' ? 'Uploading...' : (resolvedCertificate ? 'Change Certificate' : 'Upload Certificate')}
                            </label>
                          </div>
                        </div>
                      </div>

                    </div>

                    <div className="mt-2 flex justify-end border-t border-slate-100 pt-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors"
                      >
                        <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Card
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add Entry Action element block structure */}
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1 bg-blue-50 rounded-md text-blue-600">
              <FiPlus className="h-4 w-4 stroke-[3]" />
            </div>
            <h4 className="text-sm font-bold text-slate-800">Add New Entry Block</h4>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="Name * (e.g., NBA, NAAC)"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.fullName}
                onChange={(e) => setNewItem({ ...newItem, fullName: e.target.value })}
                placeholder="Full Title Name Description"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.grade}
                onChange={(e) => setNewItem({ ...newItem, grade: e.target.value })}
                placeholder="Grade / Tier (e.g., Tier-1, A+)"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row items-end gap-3">
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Short descriptive summary text outline parameters details..."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <button
                type="button"
                onClick={handleAddItem}
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
              >
                Insert Award
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Toast Notification wrapper */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AccreditationsSection;